// Dispatch detail (BUILD_SPEC.md §8.4, §9.7, §4.1). Plan.md G23. Editable while
// DRAFT (edit header/lines + Post, with the same margin preview the wizard
// shows), read-only once POSTED with a genealogy panel + delivery-note download +
// a "raise invoice now" convenience for PER_LOAD customers, and "Reverse & clone
// to draft" once POSTED per §4.1. REVERSED dispatches are a terminal read-only
// state.
import { useMemo, useState } from 'react';
import { Alert, Button, Divider, Group, Loader, Paper, Stack, Table, Text, Textarea, Title, Tooltip } from '@mantine/core';
import { Link, useNavigate } from '@tanstack/react-router';
import { IconAlertTriangle } from '@tabler/icons-react';
import type { DispatchResponse } from '@zebra/core';
import { ApiRequestError } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { ConfirmButton } from '../../components/ConfirmButton';
import { DocStatusBadge } from '../../components/DocStatusBadge';
import { CustomerName, LocationName, ProductName, SiteName } from './components/EntityNames';
import { CustomerSelect } from './components/CustomerSelect';
import { SalesOrderSelect } from './components/SalesOrderSelect';
import { SiteSelect } from './components/SiteSelect';
import { DispatchLinesEditor, dispatchLinesValid, type DispatchLineFormRow } from './components/DispatchLinesEditor';
import { DispatchMarginPreview, type MarginPreviewLine } from './components/DispatchMarginPreview';
import { DownloadPdfLink } from './components/DownloadPdfLink';
import { GenealogyPanel } from './components/GenealogyPanel';
import { centsToRand, formatDateTime, formatMoneyCents, formatQty, randToCents } from './format';
import {
  useAllProducts,
  useCreateDispatch,
  useDispatch,
  usePostDispatch,
  useRaiseInvoices,
  useReverseDispatch,
  useUpdateDispatch,
  useCustomer,
  type DispatchUpdateWire,
} from './api';

const canWriteRole = (role: string | null | undefined) => role === 'OPERATIONS' || role === 'SALES_ACCOUNTS' || role === 'ADMIN';
const canInvoiceRole = (role: string | null | undefined) => role === 'SALES_ACCOUNTS' || role === 'ADMIN';

export function DispatchDetail({ dispatchId }: { dispatchId: string }) {
  const { data: dispatch, isLoading, isError } = useDispatch(dispatchId);

  if (isLoading) return <Loader />;
  if (isError || !dispatch) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={18} />}>
        Dispatch not found.
      </Alert>
    );
  }

  return dispatch.status === 'DRAFT' ? <DraftDispatchEditor key={dispatch.id} dispatch={dispatch} /> : <PostedDispatchView dispatch={dispatch} />;
}

function toLineFormRows(dispatch: DispatchResponse): DispatchLineFormRow[] {
  return dispatch.lines.map((line) => ({
    key: line.id,
    productId: line.productId,
    productLabel: '',
    fromLocationId: line.fromLocationId,
    locationLabel: '',
    qty: line.qty,
    priceRand: centsToRand(line.priceCentsPerUnit),
    vatTreatment: line.vatTreatment,
  }));
}

function DraftDispatchEditor({ dispatch }: { dispatch: DispatchResponse }) {
  const auth = useAuth();
  const canWrite = canWriteRole(auth.activeTenant?.role);
  const updateDispatch = useUpdateDispatch(dispatch.id);
  const postDispatch = usePostDispatch(dispatch.id);
  const { data: products } = useAllProducts();
  const productsById = useMemo(() => new Map((products?.items ?? []).map((p) => [p.id, p])), [products]);

  const [siteId, setSiteId] = useState(dispatch.siteId);
  const [customerId, setCustomerId] = useState(dispatch.customerId);
  const [salesOrderId, setSalesOrderId] = useState(dispatch.salesOrderId ?? '');
  const [notes, setNotes] = useState(dispatch.notes ?? '');
  const [lines, setLines] = useState<DispatchLineFormRow[]>(() => toLineFormRows(dispatch));
  const [error, setError] = useState<string | null>(null);

  const linesValid = dispatchLinesValid(lines, productsById);

  function buildPayload(): DispatchUpdateWire {
    return {
      siteId,
      customerId,
      salesOrderId: salesOrderId || null,
      ticketId: dispatch.ticketId,
      dispatchedAt: dispatch.dispatchedAt,
      notes: notes.trim() || null,
      lines: lines.map((l, i) => ({
        lineNo: i + 1,
        productId: l.productId,
        fromLocationId: l.fromLocationId,
        qty: l.qty,
        priceCentsPerUnit: randToCents(l.priceRand),
        vatTreatment: l.vatTreatment,
      })),
    };
  }

  async function handleSave() {
    setError(null);
    try {
      await updateDispatch.mutateAsync(buildPayload());
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Failed to save changes.');
    }
  }

  async function handlePost() {
    setError(null);
    try {
      await updateDispatch.mutateAsync(buildPayload());
      await postDispatch.mutateAsync(undefined);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Failed to post the dispatch.');
    }
  }

  const previewLines: MarginPreviewLine[] = lines
    .filter((l) => l.productId && l.qty)
    .map((l) => ({ key: l.key, productId: l.productId, productLabel: l.productLabel, qty: l.qty, priceCentsPerUnit: randToCents(l.priceRand || '0') }));

  return (
    <Stack gap="lg" maw={900}>
      <Group justify="space-between">
        <Group gap="sm">
          <Title order={2}>{dispatch.number}</Title>
          <DocStatusBadge status={dispatch.status} />
        </Group>
        <Button component={Link} to="/sales-invoicing/dispatches" variant="subtle">
          Back to register
        </Button>
      </Group>

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {error}
        </Alert>
      )}

      <Paper withBorder radius="md" p="md">
        <Stack gap="md">
          <SiteSelect label="Site" value={siteId} onChange={setSiteId} required disabled={!canWrite} />
          <CustomerSelect
            label="Customer"
            value={customerId}
            onChange={(id) => {
              setCustomerId(id);
              setSalesOrderId('');
            }}
            required
            disabled={!canWrite}
          />
          <SalesOrderSelect label="Sales order (optional)" customerId={customerId || undefined} value={salesOrderId} onChange={setSalesOrderId} disabled={!canWrite} />
          <Textarea label="Notes" value={notes} onChange={(e) => setNotes(e.currentTarget.value)} autosize minRows={2} disabled={!canWrite} />
        </Stack>
      </Paper>

      <Paper withBorder radius="md" p="md">
        <DispatchLinesEditor rows={lines} onChange={setLines} siteId={siteId || undefined} productsById={productsById} />
      </Paper>

      <Paper withBorder radius="md" p="md">
        <DispatchMarginPreview siteId={siteId || undefined} lines={previewLines} />
      </Paper>

      {canWrite && (
        <Group justify="flex-end">
          <Button variant="default" onClick={handleSave} loading={updateDispatch.isPending && !postDispatch.isPending}>
            Save changes
          </Button>
          <Tooltip label="Posts this dispatch — stock leaves the ledger and the document locks.">
            <Button onClick={handlePost} loading={postDispatch.isPending} disabled={!linesValid}>
              Post
            </Button>
          </Tooltip>
        </Group>
      )}
    </Stack>
  );
}

function PostedDispatchView({ dispatch }: { dispatch: DispatchResponse }) {
  const auth = useAuth();
  const canWrite = canWriteRole(auth.activeTenant?.role);
  const canInvoice = canInvoiceRole(auth.activeTenant?.role);
  const navigate = useNavigate();
  const customer = useCustomer(dispatch.customerId);
  const reverseDispatch = useReverseDispatch(dispatch.id);
  const createDispatch = useCreateDispatch();
  const raiseInvoices = useRaiseInvoices();
  const [error, setError] = useState<string | null>(null);
  const [reversing, setReversing] = useState(false);

  async function handleReverseAndClone() {
    setError(null);
    setReversing(true);
    try {
      await reverseDispatch.mutateAsync(undefined);
      // "Clone" half of "reverse & clone to draft" (BUILD_SPEC.md §4.1): the
      // reversal only ever changes ledger movements + this document's own status,
      // so the just-reversed dispatch's own header/lines are exactly what to
      // prefill. `ticketId` is deliberately left null on the clone — the original
      // weighbridge ticket already belongs to the reversed load; a fresh dispatch
      // has no ticket until (if ever) one is entered again.
      const clone = await createDispatch.mutateAsync({
        siteId: dispatch.siteId,
        customerId: dispatch.customerId,
        salesOrderId: dispatch.salesOrderId,
        ticketId: null,
        dispatchedAt: new Date(),
        notes: dispatch.notes,
        lines: dispatch.lines.map((line, i) => ({
          lineNo: i + 1,
          productId: line.productId,
          fromLocationId: line.fromLocationId,
          qty: line.qty,
          priceCentsPerUnit: line.priceCentsPerUnit,
          vatTreatment: line.vatTreatment,
        })),
      });
      void navigate({ to: '/sales-invoicing/dispatches/$dispatchId', params: { dispatchId: clone.id } });
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Failed to reverse this dispatch.');
    } finally {
      setReversing(false);
    }
  }

  async function handleRaiseNow() {
    setError(null);
    try {
      await raiseInvoices.mutateAsync({ customerIds: [dispatch.customerId] });
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Failed to raise an invoice for this load.');
    }
  }

  const totalMargin = dispatch.lines.every((l) => l.marginCents != null)
    ? dispatch.lines.reduce((sum, l) => sum + BigInt(l.marginCents ?? '0'), 0n)
    : null;

  return (
    <Stack gap="lg" maw={900}>
      <Group justify="space-between">
        <Group gap="sm">
          <Title order={2}>{dispatch.number}</Title>
          <DocStatusBadge status={dispatch.status} />
        </Group>
        <Button component={Link} to="/sales-invoicing/dispatches" variant="subtle">
          Back to register
        </Button>
      </Group>

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {error}
        </Alert>
      )}

      <Paper withBorder radius="md" p="md">
        <Stack gap="xs">
          <Group gap={6}>
            <Text fw={600}>Site:</Text>
            <SiteName id={dispatch.siteId} />
          </Group>
          <Group gap={6}>
            <Text fw={600}>Customer:</Text>
            <CustomerName id={dispatch.customerId} />
            {customer.data && <Text c="dimmed">({customer.data.invoicingMode})</Text>}
          </Group>
          <Group gap={6}>
            <Text fw={600}>Dispatched:</Text>
            <Text>{formatDateTime(dispatch.dispatchedAt)}</Text>
          </Group>
          {dispatch.notes && (
            <Group gap={6} align="flex-start">
              <Text fw={600}>Notes:</Text>
              <Text>{dispatch.notes}</Text>
            </Group>
          )}
        </Stack>
      </Paper>

      <Paper withBorder radius="md" p="md">
        <Table.ScrollContainer minWidth={640}>
          <Table verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Product</Table.Th>
                <Table.Th>From</Table.Th>
                <Table.Th>Qty</Table.Th>
                <Table.Th ta="right">Sale value</Table.Th>
                <Table.Th ta="right">Cost</Table.Th>
                <Table.Th ta="right">Margin</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {dispatch.lines.map((line) => (
                <Table.Tr key={line.id}>
                  <Table.Td>
                    <ProductName id={line.productId} />
                  </Table.Td>
                  <Table.Td>
                    <LocationName id={line.fromLocationId} />
                  </Table.Td>
                  <Table.Td>{formatQty(line.qty)}</Table.Td>
                  <Table.Td ta="right">{formatMoneyCents(line.saleValueCents)}</Table.Td>
                  <Table.Td ta="right">{formatMoneyCents(line.costCents)}</Table.Td>
                  <Table.Td ta="right">{formatMoneyCents(line.marginCents)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        {totalMargin !== null && (
          <Group justify="flex-end" mt="sm">
            <Text fw={700}>Total margin: {formatMoneyCents(totalMargin)}</Text>
          </Group>
        )}
      </Paper>

      <Group>
        <DownloadPdfLink label="Download delivery note" pdfKey={dispatch.deliveryNoteKey} downloadPath={`/sales/dispatches/${dispatch.id}/delivery-note`} />
      </Group>

      {dispatch.status === 'POSTED' && (
        <Paper withBorder radius="md" p="md">
          <GenealogyPanel dispatchId={dispatch.id} />
        </Paper>
      )}

      {dispatch.status === 'POSTED' && (
        <>
          <Divider />
          <Group justify="space-between">
            <Group>
              {canInvoice && customer.data?.invoicingMode === 'PER_LOAD' && (
                <Tooltip label="Creates and raises an invoice for this dispatch immediately, instead of waiting for the next consolidated run.">
                  <Button variant="light" onClick={handleRaiseNow} loading={raiseInvoices.isPending}>
                    Raise invoice for this load
                  </Button>
                </Tooltip>
              )}
            </Group>
            {canWrite && (
              <ConfirmButton
                color="amber"
                variant="light"
                loading={reversing}
                label="Reverse & clone to draft"
                message={`Reverse dispatch ${dispatch.number}? Its stock and margin will be backed out by a reversing ledger entry, and a new DRAFT dispatch will be created for you to redo.`}
                onConfirm={handleReverseAndClone}
              />
            )}
          </Group>
          {raiseInvoices.isSuccess && (
            <Text size="sm" c="dimmed">
              {raiseInvoices.data.summary}
            </Text>
          )}
        </>
      )}
      {dispatch.status === 'REVERSED' && (
        <Text c="dimmed" size="sm">
          This dispatch has been reversed. Its ledger effect has been undone; see the register for any draft that
          superseded it.
        </Text>
      )}
    </Stack>
  );
}
